"use client";

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Application } from "./types";
import { ApplicationTypeKey, STATUS_STEPS, Status } from "./rules";
import { API_BASE } from "./api";
import { syncApplication, fetchStatus } from "./backendSync";

const STORAGE_KEY = "uas-registry-v1";

function uid() {
  return Math.random().toString(36).slice(2, 9).toUpperCase();
}

function seed(): Application[] {
  const now = Date.now();
  const day = 86400000;
  return [
    {
      id: "PK-UAS-" + uid(),
      type: "uas",
      title: "AgriSpray X4",
      category: "III",
      weightGrams: 8200,
      status: "Under Review",
      createdAt: now - 5 * day,
      updatedAt: now - 1 * day,
      data: { model: "X4-Agri", serial: "AGX-2201", purpose: "Agriculture" },
      flags: [
        { level: "warn", message: "Manufacturer Conformity Assessment attached, pending Security Clearance.", rule: "Rule 6(1)(a), First Schedule Cat III & IV" },
      ],
      sync: "offline",
    },
    {
      id: "PK-UAS-" + uid(),
      type: "uas",
      title: "SkyLite Mini",
      category: "I",
      weightGrams: 220,
      status: "Decided",
      createdAt: now - 20 * day,
      updatedAt: now - 18 * day,
      data: { model: "Mini-1", serial: "SL-0091", purpose: "Recreation" },
      flags: [{ level: "ok", message: "Registered — renewal due in 3 years.", rule: "Rule 4(2)" }],
      sync: "offline",
    },
    {
      id: "PK-OWN-" + uid(),
      type: "owner",
      title: "Zainab Malik",
      status: "Submitted",
      createdAt: now - 2 * day,
      updatedAt: now - 2 * day,
      data: { cnic: "35202-XXXXXXX-X", city: "Lahore" },
      flags: [],
      sync: "offline",
    },
  ];
}

interface StoreShape {
  applications: Application[];
  backendConnected: boolean;
  addApplication: (
    app: Omit<Application, "id" | "createdAt" | "updatedAt" | "status" | "sync"> & { status?: Status },
    files?: Record<string, File>
  ) => Application;
  advanceStatus: (id: string) => void;
  removeApplication: (id: string) => void;
  retrySync: (id: string) => void;
  refreshStatus: (id: string) => void;
  countByType: (type: ApplicationTypeKey) => number;
}

const StoreContext = createContext<StoreShape | null>(null);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loaded, setLoaded] = useState(false);
  // Files can't be JSON-serialized into localStorage, so pending uploads are
  // kept in memory only, keyed by application id — good for the initial
  // sync and any in-session retry; lost on a hard refresh, same as any
  // unsaved file input would be.
  const pendingFiles = useRef<Map<string, Record<string, File>>>(new Map());

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        setApplications(JSON.parse(raw));
      } else {
        const s = seed();
        setApplications(s);
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
      }
    } catch {
      setApplications(seed());
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(applications));
    } catch {
      /* ignore quota errors */
    }
  }, [applications, loaded]);

  function patch(id: string, fields: Partial<Application>) {
    setApplications((prev) => prev.map((a) => (a.id === id ? { ...a, ...fields } : a)));
  }

  async function runSync(app: Application) {
    if (!API_BASE) {
      patch(app.id, { sync: "offline" });
      return;
    }
    patch(app.id, { sync: "pending" });
    const files = pendingFiles.current.get(app.id) ?? {};
    const result = await syncApplication(app, files);
    if (result.ok) {
      patch(app.id, { sync: "synced", syncError: undefined, remoteId: result.remoteId, certificateUrl: result.certificateUrl });
    } else {
      patch(app.id, { sync: "error", syncError: result.error });
    }
  }

  const value = useMemo<StoreShape>(
    () => ({
      applications,
      backendConnected: !!API_BASE,
      addApplication: (app, files) => {
        const now = Date.now();
        const full: Application = {
          ...app,
          id: `PK-${app.type.toUpperCase().slice(0, 3)}-${uid()}`,
          status: app.status ?? "Submitted",
          createdAt: now,
          updatedAt: now,
          sync: API_BASE ? "pending" : "offline",
        };
        if (files && Object.keys(files).length > 0) {
          pendingFiles.current.set(full.id, files);
        }
        setApplications((prev) => [full, ...prev]);
        runSync(full);
        return full;
      },
      advanceStatus: (id) => {
        setApplications((prev) =>
          prev.map((a) => {
            if (a.id !== id) return a;
            const idx = STATUS_STEPS.indexOf(a.status);
            const next = STATUS_STEPS[Math.min(idx + 1, STATUS_STEPS.length - 1)];
            return { ...a, status: next, updatedAt: Date.now() };
          })
        );
      },
      removeApplication: (id) => {
        setApplications((prev) => prev.filter((a) => a.id !== id));
      },
      retrySync: (id) => {
        const app = applications.find((a) => a.id === id);
        if (app) runSync(app);
      },
      refreshStatus: async (id) => {
        const app = applications.find((a) => a.id === id);
        if (!app) return;
        const update = await fetchStatus(app);
        if (update) patch(id, update);
      },
      countByType: (type) => applications.filter((a) => a.type === type).length,
    }),
    [applications]
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}
